import { useEffect, useState, useCallback } from "react";
import api from "../api";
import "./PayrollSettings.css";

const CADENCES = [
  { value: "weekly",       label: "Weekly",       desc: "Mon–Sun; pay on following Friday" },
  { value: "bi_weekly",    label: "Bi-Weekly",    desc: "2-week blocks; pay ~10 days after period end" },
  { value: "semi_monthly", label: "Semi-Monthly", desc: "1st–15th and 16th–EOM; pay last day of month / 15th of next" },
  { value: "monthly",      label: "Monthly",      desc: "Full calendar month; pay on 15th of next month" },
];

const CADENCE_LABELS = {
  weekly:       "Weekly",
  bi_weekly:    "Bi-Weekly",
  semi_monthly: "Semi-Monthly",
  monthly:      "Monthly",
};

const TIMEOUT_PRESETS = [
  { label: "15 min",   value: 15 },
  { label: "30 min",   value: 30 },
  { label: "1 hour",   value: 60 },
  { label: "2 hours",  value: 120 },
  { label: "Disabled", value: 0 },
];

export default function PayrollSettings() {
  const [settings, setSettings]           = useState(null);
  const [los, setLos]                     = useState([]);
  const [loOverrides, setLoOverrides]     = useState({});
  const [savingCadence, setSavingCadence] = useState(false);
  const [editingLO, setEditingLO]         = useState(null);
  const [loSaving, setLoSaving]           = useState(false);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const [timeoutInput, setTimeoutInput]   = useState("");
  const [savingTimeout, setSavingTimeout] = useState(false);
  const [timeoutSaved, setTimeoutSaved]   = useState(false);

  const [weightInput, setWeightInput]   = useState("");
  const [savingWeight, setSavingWeight] = useState(false);
  const [weightSaved, setWeightSaved]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/payroll/settings"),
      api.get("/users/"),
    ]).then(([s, u]) => {
      setSettings(s.data);
      setTimeoutInput(String(s.data.inactivity_timeout_minutes ?? 30));
      setWeightInput(String(s.data.second_lien_weight ?? 0.25));
      const loanOfficers = u.data.filter(u => u.role === "loan_officer");
      setLos(loanOfficers);
      return Promise.allSettled(
        loanOfficers.map(lo => api.get(`/payroll/settings/lo/${lo.id}`))
      );
    }).then(results => {
      const overrides = {};
      results.forEach(r => {
        if (r.status === "fulfilled") {
          overrides[r.value.data.loan_officer_id] = r.value.data.cadence || null;
        }
      });
      setLoOverrides(overrides);
    }).catch(err => setError(err.response?.data?.detail || "Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveCadence = async (cadence) => {
    setSavingCadence(true);
    try {
      const { data } = await api.put("/payroll/settings", { cadence });
      setSettings(data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingCadence(false);
    }
  };

  const handleSaveTimeout = async () => {
    const minutes = parseInt(timeoutInput, 10);
    if (isNaN(minutes) || minutes < 0) { alert("Enter a valid number of minutes (0 to disable)"); return; }
    setSavingTimeout(true);
    setTimeoutSaved(false);
    try {
      const { data } = await api.put("/payroll/settings", { inactivity_timeout_minutes: minutes });
      setSettings(data);
      setTimeoutInput(String(data.inactivity_timeout_minutes));
      setTimeoutSaved(true);
      setTimeout(() => setTimeoutSaved(false), 2500);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingTimeout(false);
    }
  };

  const handleSaveWeight = async () => {
    const weight = parseFloat(weightInput);
    if (isNaN(weight) || weight < 0 || weight > 1) {
      alert("Enter a value between 0.00 and 1.00");
      return;
    }
    setSavingWeight(true);
    setWeightSaved(false);
    try {
      const { data } = await api.put("/payroll/settings", { second_lien_weight: weight });
      setSettings(data);
      setWeightInput(String(data.second_lien_weight));
      setWeightSaved(true);
      setTimeout(() => setWeightSaved(false), 2500);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingWeight(false);
    }
  };

  const handleSaveLOOverride = async (userId, cadence) => {
    setLoSaving(true);
    try {
      await api.put(`/payroll/settings/lo/${userId}`, { cadence: cadence || null });
      setLoOverrides(prev => ({ ...prev, [userId]: cadence || null }));
      setEditingLO(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save override");
    } finally {
      setLoSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading company settings...</div>;

  const currentTimeout = settings?.inactivity_timeout_minutes ?? 30;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Settings</h1>
          <p className="page-subtitle">Configure pay period cadence and session security</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* ── Session Timeout ── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Session Timeout</h2>
        <p className="settings-desc">
          Automatically log users out after a period of inactivity. Set to 0 to disable.
        </p>

        <div className="timeout-presets">
          {TIMEOUT_PRESETS.map(p => (
            <button
              key={p.value}
              className={`timeout-preset-btn ${currentTimeout === p.value ? "selected" : ""}`}
              onClick={() => setTimeoutInput(String(p.value))}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="timeout-custom-row">
          <label className="timeout-label">Custom (minutes):</label>
          <input
            type="number"
            className="timeout-input"
            min="0"
            max="480"
            value={timeoutInput}
            onChange={e => setTimeoutInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSaveTimeout()}
          />
          <button
            className="btn-primary btn-sm"
            onClick={handleSaveTimeout}
            disabled={savingTimeout || String(currentTimeout) === timeoutInput}
          >
            {savingTimeout ? "Saving..." : "Save"}
          </button>
          {timeoutSaved && <span className="timeout-saved-msg">✓ Saved</span>}
        </div>

        <p className="timeout-current-note">
          {currentTimeout === 0
            ? "Auto-logout is currently disabled."
            : `Currently: users are logged out after ${currentTimeout} minute${currentTimeout !== 1 ? "s" : ""} of inactivity.`}
        </p>
      </div>

      {/* ── Second Lien Weight ── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Second Lien Weight</h2>
        <p className="settings-desc">
          How much a second lien counts toward a retention LO's monthly loan total for tier
          calculation. Enter a value between 0 and 1 — e.g. 0.25 means one second lien
          counts as one quarter of a loan. Seconds always pay a flat 40 bps (retention)
          or 60 bps (retail) regardless of this setting.
        </p>

        <div className="timeout-custom-row">
          <label className="timeout-label">Weight (0–1):</label>
          <input
            type="number"
            className="timeout-input"
            min="0"
            max="1"
            step="0.05"
            value={weightInput}
            onChange={e => setWeightInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSaveWeight()}
            style={{ width: 90 }}
          />
          <button
            className="btn-primary btn-sm"
            onClick={handleSaveWeight}
            disabled={savingWeight || String(settings?.second_lien_weight ?? 0.25) === weightInput}
          >
            {savingWeight ? "Saving..." : "Save"}
          </button>
          {weightSaved && <span className="timeout-saved-msg">✓ Saved</span>}
        </div>

        <p className="timeout-current-note">
          Currently: one second lien counts as{" "}
          <strong>{((settings?.second_lien_weight ?? 0.25) * 100).toFixed(0)}%</strong>{" "}
          of a loan toward the monthly tier threshold.
        </p>
      </div>

      {/* ── Company cadence ── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Company-Wide Cadence</h2>
        <p className="settings-desc">Sets the default pay period schedule for all loan officers.</p>
        <div className="cadence-options">
          {CADENCES.map(c => (
            <label key={c.value} className={`cadence-option ${settings?.cadence === c.value ? "selected" : ""}`}>
              <input
                type="radio"
                name="cadence"
                value={c.value}
                checked={settings?.cadence === c.value}
                onChange={() => handleSaveCadence(c.value)}
                disabled={savingCadence}
              />
              <div className="cadence-option-text">
                <span className="cadence-label">{c.label}</span>
                <span className="cadence-desc">{c.desc}</span>
              </div>
            </label>
          ))}
        </div>
        {savingCadence && <p className="saving-indicator">Saving...</p>}
      </div>

      {/* ── LO overrides ── */}
      <div className="settings-card">
        <h2 className="settings-section-title">Per-Loan Officer Overrides</h2>
        <p className="settings-desc">Override the pay period cadence for specific loan officers. Leave blank to use the company default.</p>

        {los.length === 0 ? (
          <p className="text-muted">No loan officers found.</p>
        ) : (
          <table className="lo-override-table">
            <thead>
              <tr>
                <th>Loan Officer</th>
                <th>Cadence</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {los.map(lo => {
                const override = loOverrides[lo.id];
                const isEditing = editingLO === lo.id;
                return (
                  <tr key={lo.id}>
                    <td>
                      <span className="lo-name">{lo.full_name}</span>
                      <span className="lo-email">{lo.email}</span>
                    </td>
                    <td>
                      {isEditing ? (
                        <select
                          defaultValue={override || ""}
                          onChange={e => handleSaveLOOverride(lo.id, e.target.value || null)}
                          disabled={loSaving}
                          autoFocus
                        >
                          <option value="">Company Default ({CADENCE_LABELS[settings?.cadence] || settings?.cadence})</option>
                          {CADENCES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`override-value ${!override ? "default" : ""}`}>
                          {override ? CADENCE_LABELS[override] : `Default (${CADENCE_LABELS[settings?.cadence] || settings?.cadence})`}
                        </span>
                      )}
                    </td>
                    <td>
                      {!isEditing && (
                        <button className="btn-secondary btn-sm" onClick={() => setEditingLO(lo.id)}>
                          Edit
                        </button>
                      )}
                      {isEditing && (
                        <button className="btn-secondary btn-sm" onClick={() => setEditingLO(null)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
